import { MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import { TRL_SCALE_URL } from "@/lib/references";
import { useI18n } from "@/i18n/I18nProvider";

/** Nine-step readiness meter; segments light up in sequence once visible. */
export const TRLMeter = ({ level, compact = false }: { level: number; compact?: boolean }) => {
  const { ref, inView } = useInView<HTMLDivElement>();
  const { m } = useI18n();
  const clamped = Math.max(1, Math.min(9, Math.round(level)));
  // Stage wording: EU Horizon 2020 Work Programme, General Annexes, Part G
  const stage = m.trl.stages[clamped - 1];

  return (
    <div ref={ref} aria-label={m.trl.aria(clamped, stage)} title={m.trl.aria(clamped, stage)}>
      <div className="flex items-baseline gap-2">
        <span className="font-display font-medium text-2xl text-primary tabular-nums">TRL {clamped}</span>
        <span className="font-mono text-xs text-muted-foreground">/ 9</span>
      </div>
      <div className={cn("flex gap-[3px] mt-2", compact ? "h-1.5" : "h-2")}>
        {Array.from({ length: 9 }).map((_, i) => (
          <span key={i} className="flex-1 rounded-full bg-muted overflow-hidden">
            <span
              className={cn(
                "block h-full rounded-full bg-primary origin-left transition-transform duration-500 ease-expo-out",
                inView && i < clamped ? "scale-x-100" : "scale-x-0",
              )}
              style={{ transitionDelay: `${200 + i * 70}ms` }}
            />
          </span>
        ))}
      </div>
      {!compact && (
        <p className="text-xs text-muted-foreground mt-2 leading-snug">
          {stage}
          {m.trl.translationNote}
          <span className="block mt-1 text-muted-foreground/80">
            {m.trl.assessedBy}
          <a
            href={TRL_SCALE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-1 text-muted-foreground/80 underline decoration-dotted underline-offset-2 hover:text-primary"
          >
            {m.trl.scale}
          </a>
          </span>
        </p>
      )}
    </div>
  );
};

export interface KeyFact {
  label: string;
  value?: ReactNode;
  node?: ReactNode;
}

/** At-a-glance strip that overlaps the bottom of the hero. */
export const KeyFacts = ({ facts }: { facts: KeyFact[] }) => {
  const shown = facts.filter((f) => f.value || f.node);
  if (shown.length === 0) return null;

  return (
    <div className="relative z-10 -mt-12 md:-mt-14">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <dl className="facts-rise grid grid-cols-2 md:grid-flow-col md:auto-cols-fr gap-px overflow-hidden rounded-lg border border-border bg-border shadow-[0_24px_60px_-32px_rgba(0,0,0,0.55)]">
          {shown.map((fact, i) => (
            <div
              key={fact.label}
              className={cn(
                "bg-card px-5 py-5 md:px-7 md:py-6 min-w-0",
                shown.length % 2 === 1 && i === shown.length - 1 && "col-span-2 md:col-span-1",
              )}
            >
              <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-2">{fact.label}</dt>
              <dd className="min-w-0">
                {fact.node ?? <span className="text-[1.6rem] font-medium leading-snug break-words">{fact.value}</span>}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
};

/** Card with a soft glow that follows the pointer. */
export const SpotlightCard = ({ children, className }: { children: ReactNode; className?: string }) => {
  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };
  return (
    <div onMouseMove={onMove} className={cn("spotlight rounded-lg border border-border bg-card p-6", className)}>
      {children}
    </div>
  );
};

/** Long text that starts clamped with a soft fade and expands smoothly. */
export const ExpandableText = ({ text, lines = 5, className }: { text: string; lines?: number; className?: string }) => {
  const { m } = useI18n();
  const innerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);
  const collapsed = `${lines * 1.75}em`;

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => {
      setFullHeight(el.scrollHeight);
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 28;
      setOverflowing(el.scrollHeight > lineHeight * lines + 4);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text, lines]);

  return (
    <div className={className}>
      <div
        className={cn("overflow-hidden transition-[max-height] duration-700 ease-expo-out", overflowing && !expanded && "fade-bottom")}
        style={{ maxHeight: !overflowing ? undefined : expanded ? fullHeight : collapsed }}
      >
        <div ref={innerRef} lang="en" style={{ lineHeight: 1.75 }}>
          {text}
        </div>
      </div>
      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:gap-2.5 transition-all"
          aria-expanded={expanded}
        >
          {expanded ? m.material.showLess : m.material.readFullAbstract}
          <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-300", expanded && "rotate-180")} />
        </button>
      )}
    </div>
  );
};
